// Invoice create — Wave D.
//
// The form mirrors the legacy `sc_invoice.php` invoice creation surface:
// customer pick, line items, optional notes + dates, status, submit.
//
// PDF drag-drop is exposed as a *visual* drop-zone (matches UI_AUDIT §2.18
// "Invoice Comparison" — drag-drop PDF upload). The server-side parser
// endpoint (`POST /api/v1/invoices/compare`) is documented as a Wave D gap
// in `clients/web/docs/HANDOFF.md` so parsing isn't wired here. The
// form-driven create path is the real path of record and submits a Zod-
// validated payload to `POST /api/v1/invoices`.

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FilePlus, Plus, Trash2, Upload } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { DatePicker } from '../../components/form/DatePicker';
import { Select } from '../../components/form/Select';
import { Textarea } from '../../components/form/Textarea';
import { useCreateInvoice } from './hooks/useInvoices';
import { useToast } from '../../hooks/useToast';
import type { CreateInvoiceInput, InvoiceLineItemInput } from '../../types/api';
import styles from './InvoiceCreatePage.module.css';

interface LineItemRow extends InvoiceLineItemInput {
  /** Local-only stable key for React. */
  _key: string;
}

function newRow(): LineItemRow {
  return {
    _key: Math.random().toString(36).slice(2),
    description: '',
    quantity: 1,
    unit_price: 0,
    tax_rate: 0,
  };
}

export function InvoiceCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateInvoice();

  const [issueDate, setIssueDate] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [customerUuid, setCustomerUuid] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [status, setStatus] = useState<'draft' | 'issued'>('draft');
  const [items, setItems] = useState<LineItemRow[]>([newRow()]);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfDragOver, setPdfDragOver] = useState(false);

  const subtotal = items.reduce((acc, it) => acc + Number(it.quantity) * Number(it.unit_price), 0);
  const tax = items.reduce(
    (acc, it) =>
      acc + (Number(it.quantity) * Number(it.unit_price) * (Number(it.tax_rate) || 0)) / 100,
    0,
  );
  const total = subtotal + tax;

  function updateRow(idx: number, patch: Partial<LineItemRow>) {
    setItems((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  }

  function removeRow(idx: number) {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));
  }

  function handlePdfFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const f = files[0];
    if (!f) return;
    if (f.type !== 'application/pdf') {
      toast.error('PDF files only.');
      return;
    }
    setPdfFile(f);
    toast.info(
      'PDF attached for visual reference. Automatic line-item extraction arrives in a follow-up.',
    );
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const validRows: InvoiceLineItemInput[] = items
      .filter((row) => row.description.trim().length > 0)
      .map(({ _key: _k, ...rest }) => ({
        ...rest,
        quantity: Number(rest.quantity),
        unit_price: Number(rest.unit_price),
        tax_rate: Number(rest.tax_rate ?? 0),
      }));
    if (validRows.length === 0) {
      toast.error('Add at least one line item.');
      return;
    }
    const payload: CreateInvoiceInput = {
      items: validRows,
      status,
      notes: notes.trim() ? notes.trim() : undefined,
      issue_date: issueDate || undefined,
      due_date: dueDate || undefined,
      customer_uuid: customerUuid.trim() ? customerUuid.trim() : undefined,
    };
    create.mutate(payload, {
      onSuccess: (res) => {
        const encId = res.data?.invoice?.enc_id;
        if (encId) navigate(`/collections/invoices/${encodeURIComponent(encId)}`);
        else navigate('/collections/invoices');
      },
    });
  }

  return (
    <form className={styles.page} onSubmit={handleSubmit}>
      <PageHeader
        title="New Invoice"
        subtitle="Add line items, attach a supplier PDF, and create the invoice."
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/collections/invoices')}
            type="button"
          >
            Back
          </Button>
        }
      />

      <Card title="Supplier invoice (optional)">
        <div
          className={pdfDragOver ? styles.dropZoneActive : styles.dropZone}
          onDragOver={(e) => {
            e.preventDefault();
            setPdfDragOver(true);
          }}
          onDragLeave={() => setPdfDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setPdfDragOver(false);
            handlePdfFiles(e.dataTransfer.files);
          }}
        >
          <Upload size={28} aria-hidden />
          <p>
            <strong>Drop a supplier PDF here</strong> or use the button below.
          </p>
          <p className={styles.muted}>
            PDF only · Automatic line-item extraction coming soon — for now the file is kept
            for reference only.
          </p>
          <label className={styles.fileBtn}>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => handlePdfFiles(e.target.files)}
              aria-label="Supplier invoice PDF"
              hidden
            />
            <span>Browse for PDF…</span>
          </label>
          {pdfFile ? (
            <p className={styles.fileBadge} role="status">
              <FilePlus size={14} aria-hidden /> {pdfFile.name}
            </p>
          ) : null}
        </div>
      </Card>

      <Card title="Customer & dates">
        <div className={styles.row}>
          <label>
            Customer UUID (optional)
            <Input
              value={customerUuid}
              onChange={(e) => setCustomerUuid(e.target.value)}
              placeholder="Leave blank for unattached invoice"
              aria-label="Customer UUID"
            />
          </label>
          <label>
            Issue date
            <DatePicker
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              aria-label="Issue date"
            />
          </label>
          <label>
            Due date
            <DatePicker
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              aria-label="Due date"
            />
          </label>
        </div>
      </Card>

      <Card
        title="Line items"
        actions={
          <Button
            variant="secondary"
            icon={Plus}
            type="button"
            onClick={() => setItems((prev) => [...prev, newRow()])}
          >
            Add line
          </Button>
        }
      >
        <div className={styles.linesHeader}>
          <span>Description</span>
          <span className={styles.colNum}>Qty</span>
          <span className={styles.colNum}>Unit price</span>
          <span className={styles.colNum}>Tax %</span>
          <span className={styles.colNum}>Line total</span>
          <span aria-hidden />
        </div>
        {items.map((row, idx) => {
          const lineTotal = Number(row.quantity) * Number(row.unit_price);
          return (
            <div key={row._key} className={styles.lineRow}>
              <Input
                aria-label={`Line ${idx + 1} description`}
                value={row.description}
                onChange={(e) => updateRow(idx, { description: e.target.value })}
                placeholder="Description"
              />
              <Input
                aria-label={`Line ${idx + 1} quantity`}
                variant="number"
                min={0}
                step="0.01"
                value={String(row.quantity)}
                onChange={(e) => updateRow(idx, { quantity: Number(e.target.value) })}
              />
              <Input
                aria-label={`Line ${idx + 1} unit price`}
                variant="number"
                min={0}
                step="0.01"
                value={String(row.unit_price)}
                onChange={(e) => updateRow(idx, { unit_price: Number(e.target.value) })}
              />
              <Input
                aria-label={`Line ${idx + 1} tax rate`}
                variant="number"
                min={0}
                max={100}
                step="0.01"
                value={String(row.tax_rate ?? 0)}
                onChange={(e) => updateRow(idx, { tax_rate: Number(e.target.value) })}
              />
              <div className={styles.colNumValue}>
                {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(
                  lineTotal,
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                icon={Trash2}
                aria-label={`Remove line ${idx + 1}`}
                onClick={() => removeRow(idx)}
                type="button"
              />
            </div>
          );
        })}
        <div className={styles.totals}>
          <div>
            <span className={styles.totalsLabel}>Subtotal</span>
            <span className={styles.totalsValue}>
              {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(
                subtotal,
              )}
            </span>
          </div>
          <div>
            <span className={styles.totalsLabel}>Tax</span>
            <span className={styles.totalsValue}>
              {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(tax)}
            </span>
          </div>
          <div>
            <span className={styles.totalsLabel}>Total</span>
            <span className={styles.totalsValue}>
              {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(total)}
            </span>
          </div>
        </div>
      </Card>

      <Card title="Status & notes">
        <div className={styles.row}>
          <label>
            Initial status
            <Select
              aria-label="Initial status"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'draft' | 'issued')}
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'issued', label: 'Issued' },
              ]}
            />
          </label>
        </div>
        <Textarea
          aria-label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal notes (optional)…"
          rows={3}
        />
      </Card>

      <div className={styles.footer}>
        <Button variant="ghost" onClick={() => navigate('/collections/invoices')} type="button">
          Cancel
        </Button>
        <Button variant="primary" type="submit" loading={create.isPending}>
          Create invoice
        </Button>
      </div>
    </form>
  );
}
